import clsx from 'clsx'
import type { ReactNode } from 'react'

import { FvDateDisplay, Icon, type IconProps } from '@fv/client-components'
import { tenderDateTimeFormat, useCountdown } from '@fv/client-core'
import type {
  RouteGuideExecutionStatus,
  RouteGuideExecutionView,
} from '@fv/models'

import { Help } from '@/components/Help'
import InfoBox from '@/components/InfoBox'
import { List, ListItem } from '@/components/List'
import { Pinline } from '@/components/Pinline'
import { Price } from '@/components/shared/Price'
import { HasPermission } from '@/features/auth'

import { RouteGuideStepBadge } from './RouteGuideStepBadge'

const StepCountdown = ({ expiresAt }: { expiresAt: Date | string | null }) => {
  const { formatted, isExpired } = useCountdown(expiresAt)
  if (!formatted) return null

  return (
    <span
      className={clsx(
        'ml-2 fa-width-auto',
        isExpired
          ? 'underline underline-offset-4 decoration-red-500'
          : undefined,
      )}
    >
      <Icon icon="stopwatch" className="text-fv-gray-dark" />
      {isExpired ? (
        <span>Expired</span>
      ) : (
        <>
          <span className="font-medium underline underline-offset-4 decoration-fv-orange-bright">
            {formatted}
          </span>{' '}
          to respond
        </>
      )}
    </span>
  )
}

export type RouteGuideStep = RouteGuideExecutionView['steps'][number]

const StepRow = ({
  step,
  isCurrent,
  isSelected,
  onClick,
}: {
  step: RouteGuideStep
  isCurrent: boolean
  isSelected: boolean
  onClick?: (step: RouteGuideStep) => void
}) => {
  const content = (
    <>
      <div className="flex text-sm size-7 shrink-0 items-center justify-center rounded-full bg-fv-blue-100">
        {step.sequence}
      </div>

      <div className="flex items-center gap-x-2 overflow-hidden min-w-0">
        <span className="truncate min-w-0">{step.carrierName}</span>
      </div>
      <Pinline className="flex-1" dotted />
      <div className="text-sm text-fv-gray-dark whitespace-nowrap">
        {step.tenderedAt && (
          <span>
            Tendered{' '}
            <FvDateDisplay
              val={step.tenderedAt}
              format={tenderDateTimeFormat}
            />
          </span>
        )}
        {step.respondedAt && (
          <span className="ml-2">
            Responded{' '}
            <FvDateDisplay
              val={step.respondedAt}
              format={tenderDateTimeFormat}
            />
          </span>
        )}
        {isCurrent && step.status === 'tendered' && (
          <StepCountdown expiresAt={step.expiresAt} />
        )}
      </div>
      {step.rateAmount !== null && (
        <HasPermission name="view-rates">
          <Price
            amount={step.rateAmount}
            currency={step.rateCurrency}
            size="sm"
          />
        </HasPermission>
      )}
      <RouteGuideStepBadge status={step.status} />
    </>
  )
  const rowClassName = 'flex flex-1 min-w-0 items-center gap-x-3'

  return (
    <ListItem
      className={clsx({
        'bg-fv-beer-xlight': isCurrent,
        'hover:bg-fv-blue-25': onClick,
        ' !border-fv-orange-bright !border-b -mb-px relative z-10': isSelected,
      })}
    >
      {onClick ? (
        <button
          type="button"
          className={clsx(rowClassName, 'text-left')}
          aria-current={isSelected || undefined}
          onClick={() => onClick(step)}
        >
          {content}
        </button>
      ) : (
        <div className={rowClassName}>{content}</div>
      )}
    </ListItem>
  )
}

type Props = {
  execution: RouteGuideExecutionView
  actions?: ReactNode
  className?: string
  selectedCarrierId?: string
  onStepClick?: (step: RouteGuideStep) => void
}

const statusMessages: Partial<
  Record<
    RouteGuideExecutionStatus,
    {
      icon: IconProps['icon']
      message: (execution: RouteGuideExecutionView) => string
    }
  >
> = {
  awarded: {
    icon: 'check',
    message: execution =>
      `${execution.steps.find(s => s.status === 'accepted')?.carrierName} accepted this tender.`,
  },
  exhausted: {
    icon: 'ban',
    message: () =>
      'Every carrier on this route guide declined or timed out. Send this shipment out for spot quotes to keep it moving.',
  },
  canceled: {
    icon: 'ban',
    message: () =>
      'This shipment was canceled, so the route guide stopped tendering.',
  },
  retendered: {
    icon: 'ban',
    message: () =>
      'This shipment was retendered to another carrier, so the waterfall stopped.',
  },
}

export const RouteGuideProgress = ({
  execution,
  actions,
  className,
  selectedCarrierId,
  onStepClick,
}: Props) => {
  const statusMessage = statusMessages[execution.status]

  return (
    <div className={className}>
      <div className="mb-6 empty:hidden">{actions}</div>

      <div className="mb-6 flex items-center gap-x-2">
        <span>
          Route guide{' '}
          <Help>
            Carriers are tendered in route guide priority order, then cheapest
            first
          </Help>
        </span>

        <span>
          {execution.steps.length} contracted{' '}
          {execution.steps.length === 1 ? 'carrier' : 'carriers'}
        </span>
        <Pinline className="flex-1" dotted />
        <span>
          Started{' '}
          <FvDateDisplay
            val={execution.startedAt}
            format={tenderDateTimeFormat}
          />
        </span>
      </div>

      {statusMessage && (
        <InfoBox className="mb-4" icon={statusMessage.icon}>
          {statusMessage.message(execution)}
        </InfoBox>
      )}

      <List className="border-t border-fv-gray">
        {execution.steps.map((step, i) => (
          <StepRow
            key={step.sequence}
            step={step}
            isCurrent={
              execution.status === 'running' && i === execution.currentStepIndex
            }
            isSelected={step.carrierId === selectedCarrierId}
            onClick={onStepClick}
          />
        ))}
      </List>
    </div>
  )
}
